/**
 * tool-call-part-renderer-user Component
 *
 * User-focused rendering of tool calls showing:
 * - Explanation: Why the LLM called this tool (streamed as plain text)
 * - Tool call card with name, status, and result summary
 * - Approval/question UI when task is paused waiting for user input
 *
 * Prioritizes explanation and user understanding over technical details
 */

import React, { useState, useRef, useCallback } from 'react';
import { ToolCallPartReadAPISchema as ToolCallPart } from '@sourcepilot/generated/chat/types/toolcallpart';
import { Settings, Loader2, Check, XCircle, MessageCircleQuestion } from 'lucide-react';
import { z } from 'zod';
import { useAppHost } from '@sourcepilot/ui/framework/state/AppHostContext';
import { useNode } from '@sourcepilot/framework/relationship-manager';
import { usePartDeltaStreaming } from '@sourcepilot/ui/framework/data/dag-sync/hooks';
import { JsonCloser, StreamMode } from './ToolCallPartRenderer';
import { useAtomValue } from 'jotai';
import { taskPausedAtom } from '@sourcepilot/ui/framework/state/session-data';
import { ApprovalCard, AutoApprovedLabel, isAutoApprovedInProposal } from './approval-card';
import { QuestionForm } from './question-form';
import { PrimaryEntityLabel, ToolResultCard } from './tool-result-card';
import { getToolDisplay } from './tool-display-registry';
import { getEntityResultCard } from './entity-result-cards';
import { ErrorBoundary } from '@sourcepilot/ui/framework/ui/atoms/error-boundary';
import { newChatForProject } from './project-switch-refusal';
import { NewChatForProject } from './new-chat-for-project';

interface ToolCallPartRendererUserProps {
  part: z.infer<typeof ToolCallPart>;
  isStreaming?: boolean;
}

export function ToolCallPartRendererUser({
  part,
  isStreaming = false
}: ToolCallPartRendererUserProps) {
  const { SpawnedTaskComponent } = useAppHost();
  // Get tool name from part or args
  const toolName = part.tool_name || 'Unknown Tool';

  // Header label: frontend override (display.name) wins, then the backend
  // descriptive_name, then the formatted tool name. The override is the
  // reliable source for tools whose backend label registers lazily.
  const display = getToolDisplay(toolName);
  const displayName =
    display.name || part.descriptive_name || formatToolName(toolName);

  // JSON parser for streaming args
  const jsonParserRef = useRef<JsonCloser | null>(null);
  if (!jsonParserRef.current) {
    jsonParserRef.current = new JsonCloser(StreamMode.StreamObjectKeyValueTokens);
  }
  const closer = jsonParserRef.current;

  // State for streaming explanation
  const [streamingExplanation, setStreamingExplanation] = useState('');

  // Handle streaming delta updates
  const handleDelta = useCallback((delta: any) => {
    const delta_data = delta.delta?.delta_data;
    const delta_type = delta.delta?.delta_type;
    const args_json_delta = delta_data?.args_delta?.args_json_delta;

    if (args_json_delta && delta_type === 'tool_call_building') {
      closer.append(args_json_delta);
      const [hasChanged, streamingArgsObject] = closer.parse();
      if (hasChanged && streamingArgsObject?.explanation) {
        setStreamingExplanation(String(streamingArgsObject.explanation));
      }
    }
  }, [closer]);

  // Subscribe to streaming deltas
  usePartDeltaStreaming(
    part.is_streaming ? (part.id || null) : null,
    0,
    handleDelta
  );

  // Use streaming explanation while streaming, otherwise use final args
  const explanation = part.is_streaming ? streamingExplanation : (part.args?.explanation || '');

  // REACTIVE: Get tool call response from GraphStore
  const toolResponse = useNode((part as any).response) as any;

  // Task paused state and task reference from Jotai atom
  const { isPaused, task: activeTask } = useAtomValue(taskPausedAtom);

  // REACTIVE: Get questionnaire from GraphStore (for deferred tools)
  const questionnaire = useNode((part as any).questionnaire) as any;

  // Status logic:
  // - Streaming: part.is_streaming is true OR isStreaming prop is true (args still being received)
  // - Executing: not streaming, has args, but no ToolCallResponse yet (tool is running)
  // - Error: has a ToolCallResponse with is_error=true
  // - Complete: has a ToolCallResponse (tool finished executing and returned result)
  //
  // Special case: Result tools (final_result) are pydantic-ai's structured output mechanism.
  // They don't actually execute - they just return the final result. No ToolCallResponse is created.
  const isResultTool = toolName === 'final_result' || toolName.startsWith('final_result_');
  const isStreamingArgs = part.is_streaming || isStreaming;
  const hasToolResponse = !!toolResponse;
  const isError = hasToolResponse && toolResponse.is_error;
  // Result tools are never "executing" - they complete when streaming ends
  const isExecuting = !isResultTool && !isStreamingArgs && !!part.args && !hasToolResponse;
  // Result tools are complete when not streaming (they have no response to wait for)
  const isComplete = (hasToolResponse && !isError) || (isResultTool && !isStreamingArgs);

  // Deferred tool detection:
  // - Has questionnaire → ask_user (question form)
  // - No questionnaire but paused+executing → approval (approve/deny buttons)
  const isAwaitingResponse = isPaused && isExecuting;
  const hasQuestionnaire = !!questionnaire;
  const isApproval = isAwaitingResponse && !hasQuestionnaire;
  const isQuestions = isAwaitingResponse && hasQuestionnaire;

  // Respect show_in_ui flag - if false, only show explanation, not the tool call card
  const showToolCall = part.show_in_ui !== false;

  // Subtitle resolution cascade (header text rendered after the em-dash):
  //   1. frontend registry subtitle override (e.g. activate_skill → skill name)
  //   2. entity-shaped response → <PrimaryEntityLabel>
  //   3. nothing
  const responseContent = (toolResponse as any)?.content ?? null;
  const displayCtx = {
    args: part.args,
    response: responseContent,
  };
  const registrySubtitle = display.subtitle?.(displayCtx) ?? null;
  // a refused move to another project, or a refused write to one, offers
  // that project a chat of its own, whether the result came back as an error or not
  const newChat = hasToolResponse ? newChatForProject(responseContent) : null;
  const HeaderIcon = display.icon ?? null;

  // Per-entity-type result card (keyed by the response's `type`), rendering
  // the structured `summary` captured on the response CONTENT at write-time
  // (a snapshot of what the call did — see registry_tools `_card_summary_for`).
  // Distinct from the tool-name registry above. Only for successful
  // entity-shaped responses — error content isn't entity-shaped, so it falls
  // through to the default ToolResultCard. Legacy cards have no `summary` →
  // the card degrades to its header (never crashes).
  const entityCard = !isError ? getEntityResultCard(responseContent?.type) : null;
  const entityCardCtx = {
    content: responseContent,
    summary: responseContent?.summary ?? null,
  };
  const EntitySubtitle = entityCard?.Subtitle ?? null;
  const EntityBody = entityCard?.Body ?? null;

  return (
    <div className="my-3">
      {/* Explanation - show when available, or loading indicator while streaming */}
      {explanation ? (
        <div className="mb-3 text-sm text-gray-800 whitespace-pre-wrap break-words">
          {String(explanation)}
        </div>
      ) : isStreamingArgs ? (
        <div className="mb-3 flex items-center gap-2 text-sm text-gray-500">
          <Loader2 className="h-3 w-3 animate-spin" />
          <span>Thinking...</span>
        </div>
      ) : null}

      {/* Tool call card - only shown if show_in_ui is true and not streaming args */}
      {showToolCall && !isStreamingArgs && (
        <div className="bg-white border border-gray-200 rounded-lg p-3">
          {/* Header: Tool name and status. Inline-flow layout (icon fixed,
              then a text block) so a long subtitle wraps to the START of the
              next line instead of indenting under itself, while the tool name
              itself stays on one line (`whitespace-nowrap`). */}
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-start gap-2 min-w-0 flex-1">
              {isAwaitingResponse ? (
                <MessageCircleQuestion className="h-4 w-4 shrink-0 mt-0.5 text-amber-500" />
              ) : HeaderIcon ? (
                <HeaderIcon className="h-4 w-4 shrink-0 mt-0.5 text-gray-600" />
              ) : (
                <Settings className="h-4 w-4 shrink-0 mt-0.5 text-gray-600" />
              )}
              <div className="min-w-0 text-sm">
                <span className="font-semibold text-gray-900 whitespace-nowrap">
                  {displayName}
                  {/* A custom separator (e.g. ":") hugs the name → "Web
                      Search:". Default uses an inline em-dash below. */}
                  {registrySubtitle != null && display.separator
                    ? display.separator
                    : ''}
                </span>
                {/* Inline subtitle. Cascade:
                    1. registry/display_meta-driven subtitle (e.g. activate_skill
                       → skill name) — may render while args still streaming.
                    2. entity-shaped response → <PrimaryEntityLabel>. */}
                {registrySubtitle != null ? (
                  <span className="text-gray-700">
                    {display.separator ? ' ' : ' — '}
                    {registrySubtitle}
                  </span>
                ) : isComplete && !isQuestions && entityCard ? (
                  // A per-type card owns the header: render its subtitle if it
                  // has one, else nothing — never the bare-id label.
                  // ErrorBoundary: a stale/old-shape summary must never crash.
                  EntitySubtitle ? (
                    <ErrorBoundary fallback={() => null}>
                      {' '}
                      <EntitySubtitle {...entityCardCtx} />
                    </ErrorBoundary>
                  ) : null
                ) : isComplete &&
                  !isQuestions &&
                  (responseContent?.created === true ||
                    responseContent?.modified === true) ? (
                  // Only created/modified entity responses get the inline label;
                  // query/other results aren't entity-shaped, so the em-dash
                  // must not dangle.
                  <span className="text-gray-700">
                    {' — '}
                    <PrimaryEntityLabel content={toolResponse.content} />
                  </span>
                ) : null}
                {/* DEBUG: Show part ID for result tools */}
                {isResultTool && part.id && (
                  <span className="text-xs text-gray-400 font-mono">
                    {' '}
                    [{part.id}]
                  </span>
                )}
              </div>
            </div>
            <StatusIndicator
              isStreaming={isStreamingArgs}
              isExecuting={isExecuting && !isAwaitingResponse}
              isComplete={isComplete}
              isError={isError}
              isAwaitingResponse={isAwaitingResponse}
            />
          </div>

          {/* ran without asking because it was made inside a proposal */}
          {isAutoApprovedInProposal((part as any).pending_approval_preview) && (
            <div className="mt-1">
              <AutoApprovedLabel proposal={(part as any).pending_approval_preview.proposal} />
            </div>
          )}

          {/* Approval UI when task is paused */}
          {isAwaitingResponse && isApproval && activeTask && (
            <ApprovalCard
              task={activeTask}
              toolCallId={part.tool_call_id!}
              displayName={displayName}
              args={part.args}
              pendingApprovalPreview={
                (part as any).pending_approval_preview ?? null
              }
            />
          )}

          {/* Question form when task is paused */}
          {isAwaitingResponse && isQuestions && (
            <QuestionForm questionnaire={questionnaire} />
          )}

          {/* Read-only questions for completed ask_user calls (history) */}
          {isComplete && hasQuestionnaire && (
            <QuestionForm questionnaire={questionnaire} readOnly />
          )}

          {/* Result body — defaults to the entity-shape <ToolResultCard>.
              The registry can swap in a bespoke layout per-tool when
              the response shape doesn't fit the generic renderer. */}
          {isComplete && !isQuestions &&
            (() => {
              // The default body — also the fallback when a per-type card
              // throws (e.g. an old/changed summary shape). This is the
              // forward-evolution guarantee: a per-type card can fail or be
              // fed a stale shape and the chat NEVER crashes — it falls back
              // to the generic card. No data migration required.
              const defaultBody = (
                <ToolResultCard
                  content={toolResponse?.content}
                  isError={isError}
                  errorMessage={toolResponse?.tool_error_message}
                />
              );
              if (EntityBody) {
                return (
                  <ErrorBoundary fallback={() => defaultBody}>
                    <EntityBody {...entityCardCtx} />
                  </ErrorBoundary>
                );
              }
              if (display.resultBody) {
                return display.resultBody({ ...displayCtx, isError: !!isError });
              }
              return defaultBody;
            })()}

          {newChat && <NewChatForProject action={newChat} />}

          {/* Spawned sub-task */}
          <SpawnedTaskComponent part={part} />
        </div>
      )}
    </div>
  );
}

/**
 * Status indicator showing tool execution state with icons
 */
function StatusIndicator({
  isStreaming,
  isExecuting,
  isComplete,
  isError,
  isAwaitingResponse
}: {
  isStreaming: boolean;
  isExecuting: boolean;
  isComplete: boolean;
  isError: boolean;
  isAwaitingResponse: boolean;
}) {
  if (isAwaitingResponse) {
    return (
      <span className="text-xs text-amber-600 font-medium">Awaiting response</span>
    );
  }

  if (isStreaming || isExecuting) {
    return (
      <Loader2 className="h-4 w-4 text-amber-500 animate-spin" />
    );
  }

  if (isError) {
    return (
      <XCircle className="h-4 w-4 text-red-500" />
    );
  }

  if (isComplete) {
    return (
      <Check className="h-4 w-4 text-green-600" />
    );
  }

  return null;
}

/**
 * Format tool name from snake_case to Title Case
 */
function formatToolName(toolName: string): string {
  return toolName
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}
